import React, { useEffect, useMemo, useRef } from 'react';

import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
} from '@material-ui/core';

import { SchoolAggregationType } from 'enums';
import { getName, resizeString } from 'utils';

import { TableSearchLabel, CustomTooltip } from '../..';
import { DefineColumn, Order, CompareFn } from '../types';
import { useStyles } from './styles';

interface IEnhancedTableHeadProps {
  order: Order;
  orderBy: string;
  defineColumns: DefineColumn[];
  reloadTrigger: string | undefined;
  hiddenColumns?: string[];
  onSort(property: string, compareFunction?: CompareFn): void;
  onSearch(searchValue: string, searchParam: number | string): void;
  searchParams?: Record<string, string>;
}

const schoolAggregationType = SchoolAggregationType.BRAND;

const EnhancedTableHead: React.FC<IEnhancedTableHeadProps> = ({
  order = 'asc',
  orderBy,
  defineColumns,
  onSort,
  onSearch,
  reloadTrigger,
  hiddenColumns,
  searchParams = {},
}) => {
  const classes = useStyles();
  const tooltipRef = useRef(null);

  const columns = useMemo(() => {
    if (hiddenColumns) {
      return defineColumns.filter(column => !hiddenColumns.includes(column.id));
    }

    return defineColumns;
  }, [hiddenColumns, defineColumns]);

  useEffect(() => {
    return () => {
      return undefined;
    };
  }, [reloadTrigger]);

  return (
    <TableHead className={classes.tableHeader}>
      <TableRow>
        {columns.map(column => (
          <TableCell
            key={column.id}
            align="left"
            padding={column.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === column.id ? order : false}
            style={{
              width: column.minWidth || column.maxWidth || 'fit-content',
              minWidth: column.minWidth || 'fit-content',
              maxWidth: column.maxWidth,
            }}
          >
            {!column.hasSearch && column.hasOrder && (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : 'asc'}
                onClick={() => onSort(column.id, column?.compareFunction)}
              >
                {column.maxHeaderStringLength ? (
                  <CustomTooltip
                    ref={tooltipRef}
                    maxWidth={column.maxTooltipSize || 150}
                    labelValue={getName(
                      schoolAggregationType,
                      column.label,
                      true,
                    )}
                    showCircle={false}
                    showTitle={false}
                    minWidth={column.maxTooltipSize || 'fit-content'}
                    placement="bottom-start"
                  >
                    {resizeString(
                      getName(schoolAggregationType, column.label, true),
                      column.maxHeaderStringLength,
                    )}
                  </CustomTooltip>
                ) : (
                  getName(schoolAggregationType, column.label, true)
                )}
                {orderBy === column.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            )}

            {column.hasSearch && (
              <TableSearchLabel
                onSearch={value => onSearch(value, column.id)}
                searchParam={getName(schoolAggregationType, column.label, true)}
                searchId={column.id}
                externalSearch={searchParams[column.id]}
              >
                {column.hasOrder ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => onSort(column.id, column?.compareFunction)}
                  >
                    {column.maxHeaderStringLength ? (
                      <CustomTooltip
                        ref={tooltipRef}
                        maxWidth={column.maxTooltipSize || 150}
                        labelValue={getName(
                          schoolAggregationType,
                          column.label,
                          true,
                        )}
                        showCircle={false}
                        showTitle={false}
                        minWidth={column.maxTooltipSize || 'fit-content'}
                        placement="bottom-start"
                      >
                        {resizeString(
                          getName(schoolAggregationType, column.label, true),
                          column.maxHeaderStringLength,
                        )}
                      </CustomTooltip>
                    ) : (
                      getName(schoolAggregationType, column.label, true)
                    )}
                    {orderBy === column.id ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                ) : (
                  <>
                    {column.maxHeaderStringLength ? (
                      <CustomTooltip
                        ref={tooltipRef}
                        maxWidth={column.maxTooltipSize || 150}
                        labelValue={getName(
                          schoolAggregationType,
                          column.label,
                          true,
                        )}
                        showCircle={false}
                        showTitle={false}
                        minWidth={column.maxTooltipSize || 'fit-content'}
                        placement="bottom-start"
                      >
                        {resizeString(
                          getName(schoolAggregationType, column.label, true),
                          column.maxHeaderStringLength,
                        )}
                      </CustomTooltip>
                    ) : (
                      getName(schoolAggregationType, column.label, true)
                    )}
                  </>
                )}
              </TableSearchLabel>
            )}

            {!column.hasOrder && !column.hasSearch && (
              <>
                {column.maxHeaderStringLength ? (
                  <CustomTooltip
                    ref={tooltipRef}
                    maxWidth={column.maxTooltipSize || 150}
                    labelValue={getName(
                      schoolAggregationType,
                      column.label,
                      true,
                    )}
                    showCircle={false}
                    showTitle={false}
                    minWidth={column.maxTooltipSize || 'fit-content'}
                    placement="bottom-start"
                  >
                    {resizeString(
                      getName(schoolAggregationType, column.label, true),
                      column.maxHeaderStringLength,
                    )}
                  </CustomTooltip>
                ) : (
                  getName(schoolAggregationType, column.label, true)
                )}
              </>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default EnhancedTableHead;
